import {
  balance,
  buildGatherStartMessage,
  buildGatherCancelMessage,
  type ResourceNode,
  type SkillEntry,
  type WorkshopState,
} from "./types.ts";
import type { LocalController } from "./player.ts";
import type { WorkbenchUI } from "./workbench-ui.ts";
import type { MarketUI } from "./market-ui.ts";
import type { NetworkClient } from "./network.ts";

// Keyboard input routing for main.ts, extracted at Phase 4 Step 20.
// Owns the M / B / gather keydown+keyup switch; modal-suppression stays
// externalised so main.ts remains the orchestration point per DECISIONS
// 2026-04-18 "Phase 4 Step 15 modal suppression pattern extends from
// workbench to market". Phase 5 Step 19 retired the workshop/market
// polling lifecycle callbacks that used to ride alongside open/close.
export type InputRouterDeps = {
  target: Window;
  workbenchUI: WorkbenchUI;
  marketUI: MarketUI;
  local: LocalController;
  network: NetworkClient;
  getActiveGather: () => { nodeId: string } | null;
  getSkills: () => SkillEntry[];
  getWorkshop: () => WorkshopState;
  findNearestGatherableNode: () => ResourceNode | null;
  // Phase 5 Step 15b — combat inputs. slotIdx is 0-based (Digit1 → 0).
  onAbilitySlot: (slotIdx: number) => void;
  onBasicAttack: () => void;
  // Phase 5 Step 16b — party panel toggle + open-state getter.
  onPartyToggle: () => void;
  getPartyOpen: () => boolean;
  // Phase 5 Step 17 — expedition-ready / completed modal. ESC closes
  // the ready prompt (as decline); other keybindings are suppressed
  // while either modal is up.
  onExpeditionModalEscape: () => void;
  getExpeditionModalOpen: () => boolean;
  // Phase 6 — galaxy-map toggle.
  onGalaxyMapToggle: () => void;
  getGalaxyMapOpen: () => boolean;
  // Phase 6 Block B — player-stats panel toggle.
  onStatsToggle: () => void;
  getStatsOpen: () => boolean;
  // Phase 7 Block B — first-connect display-name modal blocks every
  // panel key so the player can't open other UI before naming themselves.
  getDisplayNameModalOpen: () => boolean;
  // Phase 7 Block C — full inventory modal (I key).
  onInventoryToggle: () => void;
  getInventoryModalOpen: () => boolean;
  // Phase 7 Block F — friends list panel (F key).
  onFriendsToggle: () => void;
  getFriendsOpen: () => boolean;
};

export function attachInputRouter(deps: InputRouterDeps): void {
  const {
    target,
    workbenchUI,
    marketUI,
    local,
    network,
    getActiveGather,
    getSkills,
    getWorkshop,
    findNearestGatherableNode,
  } = deps;

  target.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.repeat) return;
    // Phase 7 Block B — display-name modal blocks every panel key. The
    // input element inside the modal already calls stopPropagation on
    // its own listeners, so this guard catches anything that bubbles.
    if (deps.getDisplayNameModalOpen()) return;
    if (deps.getExpeditionModalOpen()) {
      if (e.code === "Escape") deps.onExpeditionModalEscape();
      return;
    }
    if (deps.getGalaxyMapOpen()) {
      if (e.code === "Escape" || e.code === balance.input.galaxyMapKey) {
        deps.onGalaxyMapToggle();
        local.setModal(false);
      }
      return;
    }
    if (deps.getStatsOpen()) {
      if (e.code === "Escape" || e.code === balance.input.statsKey) deps.onStatsToggle();
      return;
    }
    if (deps.getInventoryModalOpen()) {
      if (e.code === "Escape" || e.code === balance.input.inventoryKey) deps.onInventoryToggle();
      return;
    }
    if (deps.getFriendsOpen()) {
      if (e.code === "Escape" || e.code === balance.input.friendsKey) deps.onFriendsToggle();
      return;
    }
    if (workbenchUI.isOpen) {
      if (e.code === "Escape" || e.code === balance.input.workbenchKey) {
        workbenchUI.close();
        local.setModal(false);
      }
      return;
    }
    if (marketUI.isOpen) {
      if (e.code === "Escape" || e.code === balance.input.marketKey) {
        marketUI.close();
        local.setModal(false);
      }
      return;
    }
    if (deps.getPartyOpen()) {
      if (e.code === "Escape" || e.code === balance.input.partyKey) {
        deps.onPartyToggle();
      }
      return;
    }
    if (e.code === balance.input.galaxyMapKey) {
      local.setModal(true);
      deps.onGalaxyMapToggle();
      return;
    }
    if (e.code === balance.input.statsKey) {
      deps.onStatsToggle();
      return;
    }
    if (e.code === balance.input.inventoryKey) {
      deps.onInventoryToggle();
      return;
    }
    if (e.code === balance.input.friendsKey) {
      deps.onFriendsToggle();
      return;
    }
    if (e.code === balance.input.partyKey) {
      deps.onPartyToggle();
      return;
    }
    if (e.code === balance.input.workbenchKey) {
      local.setModal(true);
      workbenchUI.open(getSkills(), getWorkshop());
      return;
    }
    if (e.code === balance.input.marketKey) {
      local.setModal(true);
      marketUI.open();
      return;
    }
    // Phase 5 Step 15b — ability slots 1-4 + basic attack (Space).
    // Only fire when no modal is open (already guarded above).
    const slotIdx = abilitySlotIndexForCode(e.code);
    if (slotIdx !== null) {
      deps.onAbilitySlot(slotIdx);
      return;
    }
    if (e.code === balance.input.basicAttackKey) {
      deps.onBasicAttack();
      return;
    }
    if (e.code !== balance.input.gatherKey) return;
    if (getActiveGather()) return;
    const node = findNearestGatherableNode();
    if (node) network.send(buildGatherStartMessage(node.nodeId));
  });

  target.addEventListener("keyup", (e: KeyboardEvent) => {
    if (deps.getDisplayNameModalOpen()) return;
    if (workbenchUI.isOpen || marketUI.isOpen || deps.getPartyOpen() || deps.getExpeditionModalOpen() || deps.getGalaxyMapOpen() || deps.getStatsOpen() || deps.getInventoryModalOpen() || deps.getFriendsOpen()) return;
    if (e.code !== balance.input.gatherKey) return;
    // Only send gatherCancel if a gather is actually in flight — avoids
    // spamming stray cancels and matches the "hold-to-X needs keyup cancel"
    // contract in DECISIONS 2026-04-18 "gather keyup cancel gating".
    if (!getActiveGather()) return;
    network.send(buildGatherCancelMessage());
  });
}

// Maps a KeyboardEvent.code to a 0-based ability-slot index, or null
// if the code isn't one of the slot bindings.
function abilitySlotIndexForCode(code: string): number | null {
  // contracts:allow-literal — 0-based index of the ability slot.
  if (code === balance.input.abilitySlot1Key) return 0;
  if (code === balance.input.abilitySlot2Key) return 1;
  if (code === balance.input.abilitySlot3Key) return 2; // contracts:allow-literal — slot index
  if (code === balance.input.abilitySlot4Key) return 3; // contracts:allow-literal — slot index
  return null;
}
